/* bookclub-gift-wrap — vanilla custom element for BookClub's cart-drawer
 * "Make it a gift?" toggle.
 *
 * Mirrors the constructor + setCustomValidity pattern from
 * ~/Services/gift-from-label/theme-snippets/assets/gift-from-label.js.
 * Differences from the GFL PDP snippet:
 *   - surface is the cart drawer, not a product page
 *   - the form is 2 fields + a dropdown, not a single textarea
 *   - emits a SEPARATE line item (the gift-fee product) via /cart/add.js;
 *     the gift metadata rides on that line, not on the book lines
 *   - lifecycle is driven by cart events, not <form> submission
 *
 * Schema contract: online-book-store/docs/gifting-schema.md v1. Property
 * names + casing are frozen there. Pricing is a flat €1 per book: the wrap
 * fee is a single-variant product whose line QUANTITY tracks the book count
 * (computeFeeBasis() below; mirrored in PRICING.md as operator-visible truth).
 * The quantity is system-driven — the cart drawer renders it read-only so the
 * customer can't decouple it from the number of books.
 */
(function () {
  'use strict';

  // --- Constants -----------------------------------------------------------

  // Polling fallback when the theme doesn't dispatch a `cart:updated` event.
  // 2 s is slow enough to be cheap, fast enough that the fee tier updates
  // visibly before the customer hits checkout.
  var POLL_INTERVAL_MS = 2000;

  // --- Pricing -------------------------------------------------------------

  // €1 per book, flat. Mirrored in PRICING.md — keep in sync. The
  // wrap fee is a single-variant Shopify product at €1; the JS sets
  // quantity = bookCount so the Estimated Total updates via standard
  // Shopify cart math (no totals override needed).
  function computeFeeBasis(bookCount) {
    var n = Math.max(0, bookCount | 0);
    var eur = n * 1.0;
    var basisText = n + ' book' + (n === 1 ? '' : 's') + ' × €1.00';
    return { eur: eur, basis: basisText, bookCount: n };
  }

  // Variant ID lookup. `variantMap` is a JSON string keyed on book count
  // (as a string): {"1":111...,"2":111...,"3":111...,"4":111...,"5":111...}.
  // The 1-3 books tier can re-use the same variant. Missing keys fall back
  // to the highest declared key (graceful upper bound) or null (we then
  // refuse to add and surface a console warning rather than queue a
  // mis-priced order).
  function resolveVariantId(variantMap, bookCount) {
    if (!variantMap || typeof variantMap !== 'object') return null;
    var key = String(Math.max(1, bookCount | 0));
    if (variantMap[key]) return Number(variantMap[key]) || null;
    // Fall back to the highest declared count's variant.
    var keys = Object.keys(variantMap).map(Number).filter(function (k) {
      return !Number.isNaN(k);
    }).sort(function (a, b) { return a - b; });
    if (keys.length === 0) return null;
    var highest = keys[keys.length - 1];
    if ((bookCount | 0) >= highest) return Number(variantMap[String(highest)]) || null;
    return null;
  }

  // --- Cart helpers --------------------------------------------------------

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  // Book count = total cart quantity excluding the gift-fee line AND
  // the chosen-card line. Lines are identified by the `_gift_role`
  // property we stamp at add time — handle-based identification is
  // kept only as a legacy fallback for pre-property carts. Operator
  // can freely rename / replace the Shopify gift-fee product; the
  // link is whatever WE put on the line.
  function bookCountFromCart(cart, giftFeeHandle) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      if (!item) return sum;
      var props = item.properties || {};
      if (props._gift_role === 'wrap_fee' || props._gift_role === 'card') return sum;
      // Legacy fallback — pre-_gift_role carts where the wrap line was
      // tagged only by handle.
      if (giftFeeHandle && item.handle === giftFeeHandle) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }

  // Find the gift-fee (wrap) line in the cart (or null). Primary
  // identification is the `_gift_role: 'wrap_fee'` property we stamp
  // on every add via the cart-drawer; handle is a legacy fallback.
  function findGiftFeeLine(cart, giftFeeHandle) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties || {};
      if (props._gift_role === 'wrap_fee') return cart.items[i];
    }
    if (giftFeeHandle) {
      for (var j = 0; j < cart.items.length; j++) {
        if (cart.items[j].handle === giftFeeHandle) return cart.items[j];
      }
    }
    return null;
  }

  // Find the chosen-card line (a card-product we added via atomic
  // Apply, marked with _gift_role: 'card' property). Returns null when
  // no card line is in the cart (idle / customer hasn't applied yet).
  function findGiftCardLine(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties || {};
      if (props._gift_role === 'card') return cart.items[i];
    }
    return null;
  }

  function cartAdd(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (body) {
          throw new Error((body && body.description) || 'cart/add failed: ' + r.status);
        });
      }
      return r.json();
    });
  }

  function cartChange(payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (body) {
          throw new Error((body && body.description) || 'cart/change failed: ' + r.status);
        });
      }
      return r.json();
    });
  }

  // --- Settings reader -----------------------------------------------------

  function readSettings(el) {
    var rawMap = el.dataset.variantMap || '';
    var variantMap = null;
    if (rawMap) {
      try {
        variantMap = JSON.parse(rawMap);
      } catch (err) {
        console.warn('[bookclub-gift-wrap] variant_map JSON parse failed; ' +
          'gift fee will be disabled until fixed.', err, rawMap);
      }
    }
    // CPDE design map: card-product-handle → CPDE design slug. Rendered
    // server-side by the snippet's Liquid from custom.cpde_design
    // metafields. Empty map = no cards have CPDE designs (or operator
    // hasn't set the metafields yet) — falls back gracefully to the
    // sticker-message textarea only.
    var rawDesignMap = el.dataset.cardDesignMap || '';
    var cardDesignMap = {};
    if (rawDesignMap) {
      try {
        cardDesignMap = JSON.parse(rawDesignMap) || {};
      } catch (err) {
        console.warn('[bookclub-gift-wrap] card_design_map JSON parse failed; ' +
          'CPDE iframe disabled until fixed.', err, rawDesignMap);
      }
    }
    return {
      tenant:         el.dataset.tenant || 'bookclub',
      // Canonical fleet tenant key CPDE registers this store under
      // (`bookclub-com-mt.myshopify.com`), server-rendered into data-shop by
      // the snippet. Must NOT use window.Shopify.shop / shop.permanent_domain
      // here — both resolve to this store's Shopify-assigned 0y5ix1-dr.myshopify.com,
      // which CPDE rejects with 403 unknown_tenant (verified 2026-05-24). Fall
      // back to window.Shopify.shop only if data-shop is somehow absent.
      shop:           el.dataset.shop || '',
      maxChars:       parseInt(el.dataset.maxChars, 10) || 25,
      cardMaxChars:   parseInt(el.dataset.cardMaxChars, 10) || 200,
      giftFeeHandle:  el.dataset.giftFeeHandle || 'gift-fee',
      schemaVersion:  el.dataset.schemaVersion || '1',
      variantMap:     variantMap,
      cpdeOrigin:     el.dataset.cpdeOrigin || 'https://customise-engine.malta-development.com',
      cardDesignMap:  cardDesignMap,
      // Phase 3 — cart-services config source. cartServicesOrigin = the engine
      // origin to fetch per-tenant module/collection config from. cartCollections
      // = the set of collection handles for products currently in the cart
      // (Liquid-rendered, space-separated), used for collection-scope eligibility.
      cartServicesOrigin: (el.dataset.cartServicesOrigin || '').replace(/\/$/, ''),
      cartCollections: (el.dataset.cartCollections || '').split(/\s+/).filter(Boolean)
    };
  }

  // --- Design-ID generator -------------------------------------------------

  // Placeholder-mode design ID lives for the lifetime of the browser tab.
  // Once CPDE A6 ships, this string is replaced with `cpde:bookclub:<id>`
  // returned by the designer; the placeholder fallback retires when
  // schema_version bumps to 2.
  function getOrCreateDesignId() {
    try {
      var key = 'bookclub-gift-wrap.designId';
      var existing = window.sessionStorage && window.sessionStorage.getItem(key);
      if (existing) return existing;
      var uuid;
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        uuid = crypto.randomUUID();
      } else {
        // Fallback: 16 random hex chars. Good enough for an opaque ID.
        var arr = new Uint8Array(16);
        (crypto.getRandomValues || function () {})(arr);
        var hex = '';
        for (var i = 0; i < arr.length; i++) {
          var h = arr[i].toString(16);
          hex += h.length === 1 ? '0' + h : h;
        }
        uuid = hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) +
          '-' + hex.slice(16, 20) + '-' + hex.slice(20, 32);
      }
      var value = 'placeholder:' + uuid;
      if (window.sessionStorage) window.sessionStorage.setItem(key, value);
      return value;
    } catch (err) {
      // Fall back to a per-call UUID if sessionStorage is blocked.
      return 'placeholder:' + (Date.now() + '-' + Math.random().toString(36).slice(2));
    }
  }

  // --- Custom element ------------------------------------------------------

  function BookClubGiftWrap() {
    return Reflect.construct(HTMLElement, [], BookClubGiftWrap);
  }
  BookClubGiftWrap.prototype = Object.create(HTMLElement.prototype);
  BookClubGiftWrap.prototype.constructor = BookClubGiftWrap;

  BookClubGiftWrap.prototype.connectedCallback = function () {
    if (this._inited) return;
    this._inited = true;

    var self = this;
    var settings = readSettings(this);

    // Iteration 1 (2026-05-21): drawer is now a compact checkbox; the form
    // lives inside a <dialog> modal. State machine:
    //   idle      → checkbox unchecked, drawer shows just the checkbox row
    //   editing   → modal open (uncommitted) — entered via checkbox tick OR Edit btn
    //   applied   → cart has gift-fee + chosen card; drawer shows summary + Edit
    // applied flag survives modal open/close (Cancel from an Edit-reopen keeps
    // applied=true; Cancel from initial-tick reverts to idle).
    // Cart-drawer idle CTA (iteration 2.2): replaces the prior checkbox
    // with a hover-expand button. Click anywhere on it opens the modal.
    // Mobile (no hover) gets a CSS pulsate animation as the attention-
    // grabber; that's purely visual — interaction stays click-to-open.
    var cta         = this.querySelector('[data-bgw-cta]');
    var rowIdle     = this.querySelector('[data-bgw-row-idle]');
    var rowApplied  = this.querySelector('[data-bgw-row-applied]');
    var summaryText = this.querySelector('[data-bgw-summary-text]');
    var editBtn     = this.querySelector('[data-bgw-edit]');
    var modal       = this.querySelector('[data-bgw-modal]');
    var closeBtn    = this.querySelector('[data-bgw-close]');
    var cancelBtn   = this.querySelector('[data-bgw-cancel]');
    var card    = this.querySelector('[data-bgw-card]');
    var feeDisplay      = this.querySelector('[data-bgw-fee-display]');
    var feeBasisDisplay = this.querySelector('[data-bgw-fee-basis-display]');

    // Flat card-picker nodes — the grid holds every live card product
    // (built by renderCardProducts) and the empty-message surfaces when
    // no live cards exist.
    var cardProductsGrid      = this.querySelector('[data-bgw-card-products-grid]');
    var occasionEmptyMsg      = this.querySelector('[data-bgw-occasion-empty-msg]');

    // Carousel controls — desktop prev/next arrows over the horizontal card
    // rail (the rail itself is a scroll-snap container; see the snippet CSS).
    // Arrows are display:none on touch (swipe instead) and auto-hide when the
    // visible — post-filter — card set already fits without scrolling.
    var carouselPrev = this.querySelector('[data-bgw-carousel-prev]');
    var carouselNext = this.querySelector('[data-bgw-carousel-next]');

    // pickedCardProduct holds the customer's chosen specific card design
    // (variant_id + title + image_src). Distinct from `card.value` which
    // holds the occasion (e.g. "Get Well Soon").
    var pickedCardProduct = null;

    // Per-handle products cache for /collections/<handle>/products.json.
    var productsByHandle = {};

    // CPDE state (iteration 2.4) — the Customise button in Step 2
    // opens a SEPARATE <dialog> with the CPDE customise iframe stacked
    // above the main gift-wrap modal. On `cpde:design_committed` the
    // payload is captured and the dialog closes; the captured
    // personalisation rides into the card line's properties on Apply.
    // Until CPDE ships embed mode + postMessage emission per
    // CPDE-CONTRACT.md, the iframe just renders CPDE's regular page —
    // the listener sits silently and Apply ships the card with no
    // personalisation (still valid; CPDE's default render is used).
    var cpdeDialog       = this.querySelector('[data-bgw-cpde-dialog]');
    var cpdeIframe       = this.querySelector('[data-bgw-cpde-iframe]');
    var cpdeCloseBtn     = this.querySelector('[data-bgw-cpde-close]');
    var cpdePersonalisation = null;
    var cpdeCommittedDesignId = null;
    var cpdePreviewUrl = null;
    // Single-CTA (mobile-friendly) commit flow. Our footer "Save & add to
    // cart" button is the one visible CTA; it drives CPDE's commit via a
    // parent→CPDE postMessage (CPDE-CONTRACT.md §1.4) rather than relying on
    // the customer finding CPDE's own Save button inside the iframe (which
    // can sit below the fold on a phone). cpdeSavePending guards re-entry
    // while we wait for the cpde:design_committed reply; the timeout is a
    // fallback for CPDE builds that haven't shipped the request_commit
    // handler yet — we then commit from the last cpde:preview_changed state.
    var cpdeSavePending = false;
    var cpdeCommitTimeout = null;
    var CPDE_COMMIT_FALLBACK_MS = 1500;

    // CPDE-presence probe state (iteration 2.4) — null until the
    // probe resolves, then boolean. Auto-detection of CPDE-the-app:
    // if the bundle URL is reachable on the configured origin, we
    // assume CPDE is installed and route the Customise flow through
    // it. If not, the cart-drawer degrades gracefully to the
    // textarea-only fallback. Matches the eventual Shopify App
    // composition model (see ../CPDE-CONTRACT.md §3).
    var cpdeAvailable = null;
    var cpdeReadyTimeout = null;
    var CPDE_READY_TIMEOUT_MS = 4000;

    if (!cta || !modal || !card) {
      console.warn('[bookclub-gift-wrap] required nodes missing; aborting.');
      return;
    }
    if (typeof modal.showModal !== 'function') {
      console.warn('[bookclub-gift-wrap] <dialog>.showModal unsupported in this browser; the modal will not render. Polyfill not loaded.');
    }

    // applied = the cart currently holds a gift-fee line that we put there.
    // Distinct from modal.open. Tracked in JS only (not in DOM) — the canonical
    // source of truth is the cart itself, which sync() reads via /cart.js.
    var applied = false;
    // Phase 3: whether THIS cart is eligible for the gift offer per the
    // cart-services collection scope. Defaults true (strangler fallback — shown
    // until/unless cart-services explicitly scopes it out). Recomputed when
    // /config resolves (loadCartServicesConfig).
    var giftEligible = true;
    function updateDrawerRows() {
      // Idle CTA shows only when no gift is applied AND this cart is eligible.
      if (rowIdle) rowIdle.hidden = applied || !giftEligible;
      if (rowApplied) rowApplied.hidden = !applied;
    }
    function setApplied(on) { applied = !!on; updateDrawerRows(); }

    // ── Phase 3: cart-services per-tenant config (collection scoping) ─────────
    // Fetch the tenant's enabled cart modules from the cart-services engine; if
    // the gift-wrap module is present, honour its collection scope. Anything that
    // goes wrong (engine down, not configured, parse error) leaves giftEligible
    // at its safe default of true — the offer keeps showing exactly as today.
    function giftScopeEligible(scope) {
      if (!scope || scope.type === 'all') return true;
      if (scope.type === 'collections') {
        var handles = Array.isArray(scope.handles) ? scope.handles : [];
        return settings.cartCollections.some(function (h) { return handles.indexOf(h) !== -1; });
      }
      return true; // unknown scope shape → fail open (show)
    }
    function loadCartServicesConfig() {
      if (!settings.cartServicesOrigin || !settings.shop) return; // not wired → keep default
      fetch(settings.cartServicesOrigin + '/config?shop=' + encodeURIComponent(settings.shop), { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cfg) {
          if (!cfg || !Array.isArray(cfg.modules)) return; // bad shape → keep default (show)
          var gw = cfg.modules.filter(function (m) { return m && m.id === 'gift-wrap'; })[0];
          // Module ABSENT = not enabled for this tenant in cart-services → keep
          // showing (theme-driven behaviour). PRESENT = cart-services is
          // authoritative → honour its collection scope.
          if (gw) {
            giftEligible = giftScopeEligible(gw.scope);
            // cart-services is now the source of truth — override the
            // theme-provided settings with /config's where present. Each guard
            // keeps the theme value if /config omits it, so an incomplete config
            // can never break fee resolution (continuity-safe strangler).
            var s = gw.settings || {};
            if (s.gift_fee_variant_map && Object.keys(s.gift_fee_variant_map).length) settings.variantMap = s.gift_fee_variant_map;
            if (s.gift_fee_handle) settings.giftFeeHandle = s.gift_fee_handle;
            if (typeof s.max_chars === 'number' && s.max_chars > 0) settings.maxChars = s.max_chars;
            if (s.cpde_origin) settings.cpdeOrigin = String(s.cpde_origin).replace(/\/$/, '');
            updateDrawerRows();
          }
        })
        .catch(function () { /* keep giftEligible default (show) + theme settings */ });
    }

    // Property-input handles, keyed by their property name.
    var propInputs = {};
    var nodes = this.querySelectorAll('[data-bgw-prop]');
    for (var i = 0; i < nodes.length; i++) {
      propInputs[nodes[i].getAttribute('data-bgw-prop')] = nodes[i];
    }

    function setProp(name, value) {
      if (propInputs[name]) propInputs[name].value = value == null ? '' : String(value);
    }

    // Atomic visible+hidden card-property write.
    function writeCardSlug() {
      var opt = card.options[card.selectedIndex];
      var slug = (opt && opt.getAttribute('data-slug')) || '';
      setProp('_gift_card_type', slug);
    }

    // Pricing display + _gift_fee_basis write. Pure UI; the real charge
    // is whatever Shopify computed when the operator priced the variant.
    function updateFeeDisplay(bookCount) {
      var price = computeFeeBasis(bookCount);
      if (feeDisplay) feeDisplay.textContent = '€' + price.eur.toFixed(2);
      if (feeBasisDisplay) feeBasisDisplay.textContent = ' (' + price.basis + ')';
      setProp('_gift_fee_basis', price.basis);
      return price;
    }

    // --- Sync state to cart -------------------------------------------

    // Track in-flight ops so concurrent cart events don't pile up duplicate
    // /cart/add or /cart/change requests.
    var syncing = false;

    function buildProperties(price) {
      var props = {};
      // Wrap-fee role marker — load-bearing for finding this line
      // back in subsequent syncs. See findGiftFeeLine: the property
      // is the primary key, handle is only a legacy fallback.
      props['_gift_role'] = 'wrap_fee';
      // Stage 3: the per-book round sticker carries a FIXED slogan (no
      // customer sticker field anymore). GFL prints this on each book's
      // round label. The card's own personalisation rides on the card line
      // (_gift_card_personalisation, from CPDE).
      props['Gift message'] = 'Say it with a Book';
      var occasionLabel = card.value || '';
      // Prefer the picked CARD PRODUCT's title as the human-readable
      // 'Gift card' line-item property; fall back to the occasion if no
      // drill-down pick happened (defensive — Apply guard should block
      // that path).
      props['Gift card'] = (pickedCardProduct && pickedCardProduct.title) || occasionLabel;
      // Occasion slug — read from the hidden <select> option's data-slug
      // to keep this in sync with the rendered card set.
      var opt = card.options[card.selectedIndex];
      var occasionSlug = (opt && opt.getAttribute('data-slug')) || '';
      props['_gift_card_type'] = occasionSlug;
      // Real variant_id from drill-down when available; else synthetic
      // id (legacy fallback). Atomic Apply (iteration 2.2) will read
      // this back to know which card-product to add to the cart.
      props['_gift_card_design_id'] = pickedCardProduct
        ? String(pickedCardProduct.variant_id)
        : getOrCreateDesignId();
      // Placeholder mode: message body == sticker text. Diverges when CPDE
      // A6 ships and the designer returns a real design id.
      props['_gift_card_message'] = props['Gift message'];
      props['_gift_fee_basis'] = price.basis;
      props['_gift_tenant'] = settings.tenant;
      props['_gift_schema_version'] = settings.schemaVersion;
      return props;
    }

    // Iteration 2.2 — atomic Apply + €1/book quantity model.
    //
    // The gift-fee line uses a SINGLE variant; quantity tracks book
    // count via /cart/change.js (no variant-swap race window). The
    // chosen card-product is a SECOND line, identified by
    // properties._gift_role === 'card'.
    //
    // Initial Apply (no existing fee or card line, applied just
    // flipped to true, customer picked a card) fires ONE /cart/add.js
    // with both items — Estimated Total jumps in a single tick.
    //
    // Subsequent syncs (book added/removed, customer re-Edits and
    // picks a different card) adjust the lines individually with
    // /cart/change.js. This function is idempotent — given the same
    // cart + toggle state it produces the same end state.
    function cartCardProps() {
      var props = {
        _gift_role: 'card',
        _gift_tenant: settings.tenant,
        _gift_schema_version: settings.schemaVersion,
        _gift_card_design_handle: (pickedCardProduct && pickedCardProduct.handle) || '',
        _gift_card_design_title: (pickedCardProduct && pickedCardProduct.title) || ''
      };
      // CPDE personalisation (iteration 2.4) — JSON-stringified blob
      // round-tripped through the cart line so order processing /
      // fulfilment can reproduce the personalised render later. The
      // shape is opaque to the cart-drawer; CPDE owns the schema.
      if (cpdeCommittedDesignId) {
        props._gift_card_cpde_design_id = String(cpdeCommittedDesignId);
      }
      if (cpdePersonalisation) {
        try {
          props._gift_card_personalisation = JSON.stringify(cpdePersonalisation);
        } catch (e) { /* skip if non-serialisable */ }
      }
      return props;
    }

    // Tear the gift down: remove whichever of the wrap-fee / card lines are
    // present, reset the in-memory state to idle, and — crucially — re-render
    // the drawer. cart/change only mutates the cart server-side; without the
    // refresh the drawer keeps showing the removed wrap + card (operator report
    // 2026-05-26: "remove all books, the wrapping items remain"). Idempotent:
    // when called with no lines present it just normalises state and skips the
    // refresh, so the post-refresh re-init can't loop.
    function teardownAndRefresh(feeLine, cardLine) {
      // No lines to remove → nothing to tear down. Preserve pickedCardProduct
      // and `applied` — otherwise the harmless pre-open sync() inside
      // setPickedCardProduct (linked-card path, applied=false → !wantOn branch
      // → here with both null) would clobber the freshly-picked card, and the
      // customer's subsequent "Save & add to cart" would bail silently with
      // `[bookclub-gift-wrap] Add to cart: no card picked`. The customise box
      // opens (showModal runs before sync's async .then), but the add becomes
      // a no-op — operator-visible symptom 2026-05-29.
      if (!feeLine && !cardLine) return Promise.resolve();
      setApplied(false);
      pickedCardProduct = null;
      var p = Promise.resolve();
      if (feeLine)  p = p.then(function () { return cartChange({ id: feeLine.key,  quantity: 0 }); });
      if (cardLine) p = p.then(function () { return cartChange({ id: cardLine.key, quantity: 0 }); });
      p = p.then(function () { return refreshCartDrawer(); });
      return p;
    }

    function sync() {
      // A detached instance (drawer re-rendered → this element replaced by a
      // fresh one) must not keep mutating the cart. disconnectedCallback clears
      // this instance's timer + cart listeners, but this guard also neutralises
      // any straggler (e.g. the leaked window 'message' listener) as a no-op.
      if (!self.isConnected) return Promise.resolve();
      if (syncing) return Promise.resolve();
      syncing = true;
      return fetchCart().then(function (cart) {
        var bookCount = bookCountFromCart(cart, settings.giftFeeHandle);
        var price = updateFeeDisplay(bookCount);
        var existingFee  = findGiftFeeLine(cart, settings.giftFeeHandle);
        var existingCard = findGiftCardLine(cart);
        // Cart is the source of truth. If a wrap-fee line we previously added
        // is in the cart but the in-memory `applied` flag is still at its
        // init default (fresh page load, or the theme re-rendered the drawer
        // DOM and rebuilt this element), adopt applied=true. Without this,
        // init runs sync() with applied=false → the "toggle OFF" branch below
        // deletes the customer's existing gift lines, and the drawer shows the
        // idle CTA instead of the "applied" summary. (This is the behaviour the
        // init comment already promised but never implemented.)
        if (existingFee && !applied) {
          setApplied(true);
          if (summaryText) {
            var feeProps0 = existingFee.properties || {};
            var restoredLabel = feeProps0['Gift card'] ||
              (existingCard && (existingCard.product_title || existingCard.title)) || 'card';
            summaryText.textContent = 'Gift wrap applied · ' +
              (feeDisplay ? feeDisplay.textContent : '') + ' · ' + restoredLabel;
          }
        }
        // Partial-removal teardown: the gift was applied but the customer
        // deleted exactly ONE of its two lines (wrap or card) via the cart's
        // trash button. Treat that as "remove the gift" — tear down the
        // remaining line and revert to idle. Safe against false positives
        // mid-apply because the `syncing` guard blocks re-entrant syncs while
        // our own sequential add/change ops are in flight, so a fresh sync()
        // only observes a one-line cart once the customer actually removed a
        // line. (Both lines present = gift intact; neither = idle / pre-apply.)
        if (applied && (((existingFee ? 1 : 0) + (existingCard ? 1 : 0)) === 1)) {
          return teardownAndRefresh(existingFee, existingCard);
        }

        var wantOn = !!applied;
        var cardVariantId = (pickedCardProduct && Number(pickedCardProduct.variant_id)) || null;

        // Toggle OFF — remove both lines if present.
        if (!wantOn) {
          return teardownAndRefresh(existingFee, existingCard);
        }

        // No books in the cart — remove the wrap fee + card entirely; the gift
        // can't exist without a book to wrap. (Previously this parked the lines
        // at qty 0 server-side but never re-rendered, so the drawer kept showing
        // them — the bug the operator hit.)
        if (bookCount === 0) {
          return teardownAndRefresh(existingFee, existingCard);
        }

        // Restored gift after a page reload / drawer re-render: both gift
        // lines exist but the in-memory pick was lost (pickedCardProduct is
        // null). Keep the lines untouched and only re-sync the WRAP quantity
        // to the current book count — this is what makes "€1 per book" stay
        // dynamic when the customer adds/removes books on a later page view.
        // A quantity-only /cart/change.js (no `properties`) PRESERVES both
        // lines' existing props (the 'Gift card' label + the card line's CPDE
        // personalisation), which re-stamping via buildProperties / cartCardProps
        // would wipe (pickedCardProduct is null here → labels would blank out).
        if (!pickedCardProduct && existingFee && existingCard) {
          if ((existingFee.quantity || 0) !== bookCount) {
            return cartChange({ id: existingFee.key, quantity: bookCount });
          }
          return null;
        }

        // Stage 3: a card must be picked before we touch the cart (the
        // old sticker-message/card validity gate is gone — the sticker is a
        // fixed slogan now, and the card is committed via CPDE Add-to-cart).
        if (!pickedCardProduct) {
          return null;
        }

        var targetVariant = resolveVariantId(settings.variantMap, bookCount);
        if (!targetVariant) {
          console.warn('[bookclub-gift-wrap] no gift-fee variant mapped; operator must populate gift_fee_variant_map per PRICING.md §6.');
          return null;
        }

        var feeProps = buildProperties(price);

        // Initial atomic Apply — neither line exists yet AND the
        // customer has picked a specific card. One round-trip,
        // Estimated Total jumps once.
        if (!existingFee && !existingCard && cardVariantId) {
          return cartAdd({
            items: [
              { id: targetVariant,  quantity: bookCount, properties: feeProps },
              { id: cardVariantId,  quantity: 1,         properties: cartCardProps() }
            ]
          });
        }

        // Incremental — manage the two lines independently with
        // /cart/change.js. Sequential to avoid concurrent-write races.
        var ops = Promise.resolve();

        // Fee line — set quantity to current book count + re-stamp props.
        if (!existingFee) {
          ops = ops.then(function () {
            return cartAdd({ items: [{ id: targetVariant, quantity: bookCount, properties: feeProps }] });
          });
        } else {
          ops = ops.then(function () {
            return cartChange({ id: existingFee.key, quantity: bookCount, properties: feeProps });
          });
        }

        // Card line — add if missing, swap variant if customer changed
        // their pick, remove if the customer somehow cleared the pick.
        if (cardVariantId) {
          if (!existingCard) {
            ops = ops.then(function () {
              return cartAdd({ items: [{ id: cardVariantId, quantity: 1, properties: cartCardProps() }] });
            });
          } else if (Number(existingCard.variant_id) !== cardVariantId) {
            ops = ops.then(function () { return cartChange({ id: existingCard.key, quantity: 0 }); });
            ops = ops.then(function () {
              return cartAdd({ items: [{ id: cardVariantId, quantity: 1, properties: cartCardProps() }] });
            });
          } else {
            // Same card, refresh properties (in case message / tenant changed).
            ops = ops.then(function () {
              return cartChange({ id: existingCard.key, quantity: 1, properties: cartCardProps() });
            });
          }
        } else if (existingCard) {
          ops = ops.then(function () { return cartChange({ id: existingCard.key, quantity: 0 }); });
        }

        return ops;
      }).catch(function (err) {
        console.warn('[bookclub-gift-wrap] sync failed:', err);
      }).then(function () {
        syncing = false;
        // After any mutation Shopify themes typically dispatch
        // `cart:updated`; if this theme doesn't, the poll below catches up.
      });
    }

    // --- Wire-up -------------------------------------------------------

    // ── Modal open/close + Apply/Cancel/Edit wire-up ─────────────────
    //
    // Drawer checkbox: ticking opens the modal (and primes `editing`).
    //                  unticking (only reachable when applied=false) is a
    //                  no-op. When applied=true the drawer hides the
    //                  checkbox row and shows the summary row instead.
    // Modal Apply:     validate → applied=true → sync cart → close modal.
    // Modal Cancel/✕/Esc:
    //                  close modal. If applied=false (initial flow), revert
    //                  the checkbox to unticked. If applied=true (Edit
    //                  reopen), keep state unchanged.
    // Drawer Edit:     re-open the modal preserving current form values.

    function openModal() {
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
      // Single in-modal step: the card picker. Personalise + Add-to-cart
      // happen in the CPDE dialog (Stage 3). Edit reopens here so the customer
      // can re-pick / re-personalise.
      // Stage 2: fetch every occasion's cards into one flat grid + build
      // the occasion filter chips (once; cached for the element's life).
      loadAllCardsOnce();
      // Re-evaluate the carousel arrows on every open — on a re-open the rail
      // is already populated (loadAllCardsOnce no-ops) so renderCardProducts
      // won't run, but the dialog now has layout again and the arrow state
      // needs recomputing against the current clientWidth.
      updateCarouselArrows();
    }

    function clearPickedCardProduct() {
      pickedCardProduct = null;
      setProp('_gift_card_design_id', getOrCreateDesignId());
      // Uncheck any selected card-product radio so the green border
      // doesn't persist across an occasion change.
      var checked = self.querySelector('[data-bgw-card-product-radio]:checked');
      if (checked) checked.checked = false;
    }

    function setPickedCardProduct(product) {
      pickedCardProduct = product;
      // Reflect the card's occasion onto the hidden <select> so
      // writeCardSlug()/buildProperties resolve _gift_card_type (occasion
      // comes from the chosen card — no occasion radio).
      if (product.occasionLabel) { card.value = product.occasionLabel; writeCardSlug(); }
      setProp('_gift_card_design_id', String(product.variant_id));
      // New card → reset CPDE personalisation + the Add-to-cart gate.
      cpdePersonalisation = null;
      cpdeCommittedDesignId = null;
      cpdePreviewUrl = null;
      resetCpdeAddCart();
      // Linked to a CPDE design → the customiser IS the personalise step, open
      // it directly (the actual add-to-cart happens on its Save & add button).
      // The pre-sync here is a harmless applied=false pass; the real add comes
      // later. NOT linked (a live card the operator hasn't wired to a CPDE
      // design) → there's no customiser to open, so add the card as-is
      // alongside the wrap right away rather than dead-ending the pick. Such a
      // card carries no personalisation payload — fulfilment prints the card's
      // own artwork. Link a CPDE design to make it personalisable.
      var slug = settings.cardDesignMap[product.handle];
      if (slug && cpdeAvailable !== false) {
        sync();
        openCpdeDialog();
      } else {
        finalizeGiftAddToCart();
      }
    }

    // ── CPDE customise dialog (iteration 2.4) ───────────────────────
    //
    // Step 2 shows a card preview + Customise button. Clicking it
    // opens a SEPARATE <dialog> stacked above the main gift-wrap
    // modal with the CPDE customise iframe inside, sized to give
    // CPDE roughly the same room as its native PDP customise page.
    //
    // On `cpde:design_committed` postMessage, the personalisation +
    // committed design id are captured, the CPDE dialog closes, and
    // the Step 2 card preview updates to the latest preview URL
    // (CPDE renders a personalised thumbnail). The captured payload
    // round-trips into the card line's properties on Apply.

    function buildCpdeUrl(designSlug) {
      var origin = settings.cpdeOrigin.replace(/\/$/, '');
      var params = new URLSearchParams({
        tenant: settings.tenant,
        shop: settings.shop || (window.Shopify && window.Shopify.shop) || '',
        frame_origin: window.location.origin
      });
      // The /embed/:slug route is governed by CSP frame-ancestors (not
      // CORS) — see ../CPDE-CONTRACT.md §1. The iframe loads it directly;
      // the postMessage handshake (not a pre-flight) decides success.
      return origin + '/embed/' + encodeURIComponent(designSlug) + '?' + params.toString();
    }

    // Runtime CPDE-presence probe. Fires once on snippet init; cached
    // for the modal lifetime. HEADs the bundle URL — if CPDE is
    // installed + reachable, the response is 200. If not (CPDE not
    // installed, tunnel down, CORS blocks the HEAD, etc.), we treat
    // CPDE as absent. Cached in cpdeAvailable; gates openCpdeDialog() so
    // a card whose CPDE design is unreachable won't open the customiser.
    function probeCpde() {
      if (cpdeAvailable !== null) return Promise.resolve(cpdeAvailable);
      var url = settings.cpdeOrigin.replace(/\/$/, '') + '/storefront/embed.js';
      return fetch(url, { method: 'HEAD', cache: 'no-store', mode: 'cors' })
        .then(function (r) {
          cpdeAvailable = r.ok;
          return cpdeAvailable;
        })
        .catch(function () {
          cpdeAvailable = false;
          return false;
        });
    }

    function openCpdeDialog() {
      if (!cpdeDialog || !cpdeIframe || !pickedCardProduct) return;
      var slug = settings.cardDesignMap[pickedCardProduct.handle];
      if (!slug) return;
      // Open the iframe directly — do NOT pre-flight /embed/:slug with a
      // CORS fetch. That route is a same-site HTML page whose framing is
      // governed by CSP `frame-ancestors`, NOT CORS, so it (correctly)
      // serves no `Access-Control-Allow-Origin`. A `mode:'cors'` HEAD is
      // therefore blocked by the browser and the promise rejects even when
      // the route is healthy (verified 2026-05-24 — /embed/:slug returns
      // 200 + frame-ancestors but no ACAO; the old HEAD here forced an
      // unconditional textarea fallback). Iframes don't need CORS.
      //
      // Success/failure is decided by the postMessage handshake instead:
      //   • `cpde:ready` (CPDE-CONTRACT.md §1.3) confirms the widget mounted
      //     → clears CPDE_READY_TIMEOUT and the dialog stays open.
      //   • If no `cpde:ready` arrives within CPDE_READY_TIMEOUT_MS (route
      //     down, edge serving a stale handshake-less bundle, embed mode
      //     not emitting), the timeout in actuallyOpenCpdeDialog() closes
      //     the dialog and reverts to the textarea fallback.
      // CPDE reachability is still gated upstream by probeCpde() (which
      // HEADs /storefront/embed.js — that asset IS CORS-enabled).
      actuallyOpenCpdeDialog(buildCpdeUrl(slug));
    }

    function actuallyOpenCpdeDialog(url) {
      cpdeDialog.classList.remove('bgw-cpde-dialog--ready');
      cpdeDialog.classList.remove('bgw-cpde-dialog--working');
      // Each open starts with Add-to-cart disabled — the iframe reloads
      // fresh, so the customer re-Saves to (re-)enable it.
      resetCpdeAddCart();
      if (cpdeIframe.src !== url) cpdeIframe.src = url;
      if (typeof cpdeDialog.showModal === 'function') cpdeDialog.showModal();
      else cpdeDialog.setAttribute('open', '');
      // Belt-and-braces timeout — if the embed page loads but cpde:ready
      // never arrives (route down, edge serving a stale handshake-less
      // bundle, or embed mode not emitting postMessages yet), close after
      // a few seconds and use the textarea fallback. The customer sees the
      // embed UI in the meantime in case it's just slow to signal.
      if (cpdeReadyTimeout) clearTimeout(cpdeReadyTimeout);
      cpdeReadyTimeout = setTimeout(function () {
        if (cpdeDialog.classList.contains('bgw-cpde-dialog--working')) return;
        console.warn('[bookclub-gift-wrap] CPDE did not signal ready within ' +
          CPDE_READY_TIMEOUT_MS + 'ms; falling back to textarea.');
        cpdeAvailable = false;
        closeCpdeDialog();
      }, CPDE_READY_TIMEOUT_MS);
    }

    function closeCpdeDialog() {
      if (!cpdeDialog) return;
      if (typeof cpdeDialog.close === 'function') cpdeDialog.close();
      else cpdeDialog.removeAttribute('open');
    }

    function unmountCpde() {
      closeCpdeDialog();
      if (cpdeIframe) cpdeIframe.removeAttribute('src');
      cpdePersonalisation = null;
      cpdeCommittedDesignId = null;
      cpdePreviewUrl = null;
    }

    if (cpdeCloseBtn) cpdeCloseBtn.addEventListener('click', closeCpdeDialog);

    // ── Stage 3: "Add to cart" inside the CPDE dialog ───────────────
    // The CPDE viewer IS the personalise step (no separate Step 2 page).
    // This button is disabled until the customer hits Save in CPDE
    // (cpde:design_committed enables it); clicking it commits the whole
    // gift — wrap + this card carrying the CPDE personalisation — in one
    // atomic /cart/add.js, then closes everything.
    var cpdeAddCartBtn = self.querySelector('[data-bgw-cpde-addcart]');
    var cpdeHint       = self.querySelector('[data-bgw-cpde-hint]');

    function resetCpdeAddCart() {
      cpdeSavePending = false;
      if (cpdeCommitTimeout) { clearTimeout(cpdeCommitTimeout); cpdeCommitTimeout = null; }
      // Disabled only until the embed is interactive (cpde:ready / iframe
      // load enables it); from then on it's the single Save & add CTA.
      if (cpdeAddCartBtn) cpdeAddCartBtn.disabled = true;
      if (cpdeHint) cpdeHint.textContent = 'Loading your card…';
    }

    // Enable the single CTA once the embed is interactive. Called from both
    // the cpde:ready postMessage and the native iframe-load fallback so the
    // button works even on CPDE builds that don't emit the handshake.
    function enableCpdeAddCart() {
      if (cpdeSavePending) return;            // mid-save — leave the working state
      if (cpdeAddCartBtn) cpdeAddCartBtn.disabled = false;
      if (cpdeHint) cpdeHint.textContent = 'Personalise above, then tap to save';
    }

    // Parent → CPDE postMessage (CPDE-CONTRACT.md §1.4). targetOrigin is the
    // configured CPDE origin so the message can't leak to another frame.
    function postToCpde(msg) {
      if (!cpdeIframe || !cpdeIframe.contentWindow) return;
      msg.source = 'bookclub-gift-wrap';
      try { cpdeIframe.contentWindow.postMessage(msg, settings.cpdeOrigin); }
      catch (e) { console.warn('[bookclub-gift-wrap] postToCpde failed:', e && e.message); }
    }

    // Commit the whole gift — book wrap fee + the chosen card carrying the
    // CPDE personalisation — in one atomic /cart/add.js, then tear down.
    function finalizeGiftAddToCart() {
      if (!pickedCardProduct) { console.warn('[bookclub-gift-wrap] Add to cart: no card picked.'); return; }
      setApplied(true);
      if (summaryText) {
        summaryText.textContent = 'Gift wrap applied · ' + (pickedCardProduct.title || 'card');
      }
      closeCpdeDialog();
      closeModal();
      Promise.resolve(sync()).then(refreshCartDrawer);
    }

    // Single-CTA path: ask the embed to commit, then add to cart on its
    // cpde:design_committed reply. Falls back to the last preview state if
    // CPDE hasn't shipped the request_commit handler so the button still
    // works today (design_id degrades to the design slug — see js below).
    function requestCpdeCommitThenAdd() {
      if (cpdeSavePending) return;
      cpdeSavePending = true;
      if (cpdeAddCartBtn) cpdeAddCartBtn.disabled = true;
      if (cpdeHint) cpdeHint.textContent = 'Saving your card…';
      postToCpde({ type: 'cpde:request_commit' });
      cpdeCommitTimeout = setTimeout(function () {
        if (!cpdeSavePending) return;          // a real commit already landed
        cpdeSavePending = false;
        cpdeCommitTimeout = null;
        // No commit ack — use whatever CPDE last emitted via preview_changed
        // (captured defensively below) and fall back design_id → slug.
        if (!cpdeCommittedDesignId) {
          cpdeCommittedDesignId =
            (pickedCardProduct && settings.cardDesignMap[pickedCardProduct.handle]) || null;
        }
        finalizeGiftAddToCart();
      }, CPDE_COMMIT_FALLBACK_MS);
    }

    // Our custom /cart/add.js mutates the cart server-side, but Dawn's cart
    // drawer doesn't know about it — so the new gift lines wouldn't appear
    // until a reload. Re-render the drawer via Dawn's own renderContents
    // (Section Rendering API) so the book + Gift Wrapping + card show up and
    // the drawer reopens.
    function refreshCartDrawer() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return Promise.resolve();
      return fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sections) { if (sections) drawer.renderContents({ sections: sections }); })
        .catch(function (e) { console.warn('[bookclub-gift-wrap] cart-drawer refresh failed:', e && e.message); });
    }

    if (cpdeAddCartBtn) {
      cpdeAddCartBtn.addEventListener('click', function () {
        if (!pickedCardProduct) { console.warn('[bookclub-gift-wrap] Add to cart: no card picked.'); return; }
        // If the customer already hit CPDE's own Save (design committed),
        // add straight away. Otherwise this single CTA drives the commit.
        if (cpdeCommittedDesignId) finalizeGiftAddToCart();
        else requestCpdeCommitThenAdd();
      });
    }

    // Native iframe-load fallback — drops the "Loading personalisation…"
    // overlay as soon as the iframe's document load event fires, in
    // addition to the cpde:ready postMessage path. Without this the
    // overlay stays opaque forever for any CPDE build that hasn't
    // shipped the postMessage emit yet (i.e. all current builds until
    // CPDE-CONTRACT.md §2 lands).
    if (cpdeIframe) {
      cpdeIframe.addEventListener('load', function () {
        if (cpdeDialog) cpdeDialog.classList.add('bgw-cpde-dialog--ready');
        // Single-CTA: enable our Save & add button even on CPDE builds that
        // never emit cpde:ready (the postMessage path enables it too).
        enableCpdeAddCart();
      });
    }

    // PostMessage listener — only acts on messages from the configured
    // CPDE origin to avoid trusting arbitrary postMessage sources.
    window.addEventListener('message', function (event) {
      if (event.origin !== settings.cpdeOrigin) return;
      var data = event.data;
      if (!data || data.source !== 'cpde' || typeof data.type !== 'string') return;

      switch (data.type) {
        case 'cpde:ready':
          if (cpdeDialog) {
            cpdeDialog.classList.add('bgw-cpde-dialog--ready');
            cpdeDialog.classList.add('bgw-cpde-dialog--working');
          }
          if (cpdeReadyTimeout) { clearTimeout(cpdeReadyTimeout); cpdeReadyTimeout = null; }
          enableCpdeAddCart();
          break;

        case 'cpde:preview_changed':
          // Capture incremental personalisation so a network blip or
          // close-without-commit still ships *some* personalisation.
          cpdePersonalisation = data.personalisation || null;
          if (data.preview_url) cpdePreviewUrl = data.preview_url;
          break;

        case 'cpde:design_committed':
          cpdePersonalisation = data.personalisation || null;
          cpdeCommittedDesignId = data.design_id || data.design_slug || null;
          if (data.preview_url) cpdePreviewUrl = data.preview_url;
          if (cpdeCommitTimeout) { clearTimeout(cpdeCommitTimeout); cpdeCommitTimeout = null; }
          if (cpdeSavePending) {
            // Commit was driven by our single CTA → finish the add-to-cart now.
            cpdeSavePending = false;
            finalizeGiftAddToCart();
          } else {
            // Customer hit CPDE's own Save first — our CTA becomes the next step.
            if (cpdeAddCartBtn) cpdeAddCartBtn.disabled = false;
            if (cpdeHint) cpdeHint.textContent = 'Saved — tap to add your gift';
          }
          break;

        case 'cpde:error':
          console.warn('[bookclub-gift-wrap] CPDE error:', data.code, data.message);
          if (cpdeDialog) cpdeDialog.classList.remove('bgw-cpde-dialog--ready');
          if (cpdeSavePending) {
            // A commit we requested failed (e.g. missing required text) —
            // surface it and re-arm the CTA instead of leaving it stuck.
            cpdeSavePending = false;
            if (cpdeCommitTimeout) { clearTimeout(cpdeCommitTimeout); cpdeCommitTimeout = null; }
            if (cpdeAddCartBtn) cpdeAddCartBtn.disabled = false;
            if (cpdeHint) cpdeHint.textContent = data.message || 'Please finish your card, then tap again';
          }
          break;
      }
    });

    function fetchProductsForCollection(handle) {
      if (productsByHandle[handle]) return Promise.resolve(productsByHandle[handle]);
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json', {
        credentials: 'same-origin'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var products = (data && Array.isArray(data.products)) ? data.products : [];
          productsByHandle[handle] = products;
          return products;
        })
        .catch(function () {
          productsByHandle[handle] = [];
          return [];
        });
    }

    function renderCardProducts(products) {
      if (!cardProductsGrid) return;
      // Wipe + rebuild — simpler than diffing for a typical N≤6 grid.
      while (cardProductsGrid.firstChild) cardProductsGrid.removeChild(cardProductsGrid.firstChild);

      products.forEach(function (p) {
        if (!p || !Array.isArray(p.variants) || p.variants.length === 0) return;
        // Every live card in the offered collections renders. Whether picking
        // it opens the CPDE customiser depends on its custom.cpde_design link
        // (handled in setPickedCardProduct) — the link no longer gates whether
        // the card is shown.
        var variant = p.variants[0];
        var imgSrc = (Array.isArray(p.images) && p.images[0] && p.images[0].src) ||
                     (p.featured_image) || '';
        // Shopify returns price as string in major units (e.g. "1.00").
        var priceText = '';
        if (variant.price) priceText = '€' + Number(variant.price).toFixed(2);

        var label = document.createElement('label');
        label.className = 'bgw-card-product';
        // Tag the tile with its occasion handle so the filter chips can
        // show/hide it (Stage 2 flat grid).
        if (p.__occasion) label.setAttribute('data-occasion', p.__occasion.handle);

        var radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'bgw-card-product-radio';
        radio.setAttribute('data-bgw-card-product-radio', '');
        radio.value = String(variant.id);

        var media = document.createElement('span');
        media.className = 'bgw-card-product__media';
        if (imgSrc) media.style.backgroundImage = 'url(' + imgSrc + ')';

        var title = document.createElement('span');
        title.className = 'bgw-card-product__title';
        title.textContent = p.title || '';

        var price = document.createElement('span');
        price.className = 'bgw-card-product__price';
        price.textContent = priceText;

        label.appendChild(radio);
        label.appendChild(media);
        label.appendChild(title);
        if (priceText) label.appendChild(price);

        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          setPickedCardProduct({
            variant_id: variant.id,
            title: p.title || '',
            image_src: imgSrc,
            handle: p.handle || '',
            price: variant.price || '',
            occasionLabel: (p.__occasion && p.__occasion.label) || '',
            occasionSlug: (p.__occasion && p.__occasion.slug) || ''
          });
        });
        // Re-clicking the already-selected card doesn't fire 'change', so
        // re-open the CPDE customiser here (without resetting the captured
        // personalisation).
        radio.addEventListener('click', function () {
          if (pickedCardProduct && String(pickedCardProduct.variant_id) === radio.value) {
            openCpdeDialog();
          }
        });

        cardProductsGrid.appendChild(label);
      });

      // New card set rendered — reset the rail to the start and recompute
      // which arrows apply (the "All" set usually overflows, a small
      // occasion may not).
      cardProductsGrid.scrollLeft = 0;
      updateCarouselArrows();
    }

    // ── Flat card picker (Stage 2) ──────────────────────────────────
    // Fetch every occasion collection's products once on first open,
    // render them into ONE grid, and build occasion filter chips for the
    // occasions that actually have cards ("All" default). Replaces the
    // carousel + per-occasion drill-down.
    var allCardsLoaded = false;
    var occasionChips  = self.querySelector('[data-bgw-occasion-chips]');

    function occasionsFromSelect() {
      // The hidden <select> is the canonical occasion → {label,slug,handle} map.
      var out = [];
      for (var i = 0; i < card.options.length; i++) {
        var o = card.options[i];
        var handle = o.getAttribute('data-collection-handle');
        if (!handle) continue;
        out.push({ handle: handle, label: o.value, slug: o.getAttribute('data-slug') || '' });
      }
      return out;
    }

    function filterByOccasion(handle, activeChip) {
      if (occasionChips) {
        var chips = occasionChips.querySelectorAll('.bgw-chip');
        Array.prototype.forEach.call(chips, function (c) {
          var on = (c === activeChip);
          c.classList.toggle('bgw-chip--active', on);
          c.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
      if (!cardProductsGrid) return;
      var tiles = cardProductsGrid.querySelectorAll('.bgw-card-product');
      Array.prototype.forEach.call(tiles, function (t) {
        t.hidden = (handle !== '__all' && t.getAttribute('data-occasion') !== handle);
      });
      // Filtering changes the rail's content width — jump back to the start
      // and re-evaluate the arrows for the new visible set.
      cardProductsGrid.scrollLeft = 0;
      updateCarouselArrows();
    }

    function renderOccasionChips(liveOccasions) {
      if (!occasionChips) return;
      while (occasionChips.firstChild) occasionChips.removeChild(occasionChips.firstChild);
      // "All" first (default active), then one chip per occasion with cards.
      var defs = [{ handle: '__all', label: 'All' }].concat(liveOccasions);
      defs.forEach(function (occ, idx) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bgw-chip' + (idx === 0 ? ' bgw-chip--active' : '');
        chip.setAttribute('data-bgw-chip', occ.handle);
        chip.setAttribute('role', 'tab');
        chip.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
        chip.textContent = occ.label;
        chip.addEventListener('click', function () { filterByOccasion(occ.handle, chip); });
        occasionChips.appendChild(chip);
      });
      // Only show the chip row when there's more than one occasion to filter.
      occasionChips.hidden = (liveOccasions.length < 2);
    }

    function loadAllCardsOnce() {
      if (allCardsLoaded) return;
      allCardsLoaded = true;
      var occasions = occasionsFromSelect();
      Promise.all(occasions.map(function (occ) {
        return fetchProductsForCollection(occ.handle).then(function (products) {
          return { occ: occ, products: products || [] };
        });
      })).then(function (results) {
        var merged = [];
        var live = [];
        results.forEach(function (r) {
          // Show every live card in the offered collection (operator directive
          // 2026-05-26 — "the cart should pick up all the cards in the
          // collections"). The old custom.cpde_design filter hid live products
          // that simply weren't linked to a CPDE design yet; that link governs
          // in-cart personalisation (see setPickedCardProduct), not collection
          // membership / visibility.
          var ready = r.products.filter(function (p) {
            return p && Array.isArray(p.variants) && p.variants.length;
          });
          if (ready.length) {
            live.push(r.occ);
            ready.forEach(function (p) { p.__occasion = r.occ; merged.push(p); });
          }
        });
        renderOccasionChips(live);
        renderCardProducts(merged);
        if (occasionEmptyMsg) occasionEmptyMsg.hidden = (merged.length > 0);
      });
    }

    // ── Card carousel arrows (desktop) ──────────────────────────────
    // The card rail scrolls horizontally; these drive it for mouse users.
    // They auto-hide when the visible (post-filter) set already fits, and
    // disable at each end of the scroll. On touch the arrows are CSS-hidden
    // and the customer swipes.
    function updateCarouselArrows() {
      if (!cardProductsGrid || !carouselPrev || !carouselNext) return;
      // Reading scrollWidth/clientWidth forces the reflow that reflects any
      // just-applied occasion filter (hidden tiles leave the flex row).
      var overflowing = (cardProductsGrid.scrollWidth - cardProductsGrid.clientWidth) > 4;
      carouselPrev.hidden = !overflowing;
      carouselNext.hidden = !overflowing;
      if (!overflowing) return;
      var x = cardProductsGrid.scrollLeft;
      carouselPrev.disabled = x <= 1;
      carouselNext.disabled = (x + cardProductsGrid.clientWidth) >= (cardProductsGrid.scrollWidth - 1);
    }

    function scrollCarousel(dir) {
      if (!cardProductsGrid) return;
      // Page by ~80% of the visible width so a card stays on-screen as an anchor.
      var step = Math.max(170, Math.round(cardProductsGrid.clientWidth * 0.8));
      if (typeof cardProductsGrid.scrollBy === 'function') {
        cardProductsGrid.scrollBy({ left: dir * step, behavior: 'smooth' });
      } else {
        cardProductsGrid.scrollLeft += dir * step;
      }
    }

    if (carouselPrev) carouselPrev.addEventListener('click', function () { scrollCarousel(-1); });
    if (carouselNext) carouselNext.addEventListener('click', function () { scrollCarousel(1); });
    if (cardProductsGrid) {
      cardProductsGrid.addEventListener('scroll', updateCarouselArrows, { passive: true });
    }

    function closeModal() {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    }

    cta.addEventListener('click', function () { openModal(); });

    if (editBtn) editBtn.addEventListener('click', function () { openModal(); });

    function cancel() {
      closeModal();
      if (!applied) {
        // Initial-flow cancel — reset the card pick + tear down the CPDE
        // iframe so a re-open lands cleanly on a fresh card picker.
        clearPickedCardProduct();
        unmountCpde();
      }
    }
    if (closeBtn)  closeBtn.addEventListener('click', cancel);
    if (cancelBtn) cancelBtn.addEventListener('click', cancel);
    modal.addEventListener('close', function () {
      // Browser-fired close (Esc, click backdrop in some browsers, etc.).
      if (!applied) {
        clearPickedCardProduct();
        unmountCpde();
      }
    });

    card.addEventListener('change', function () {
      writeCardSlug();
      sync();
    });

    // Cart event listeners — themes vary on which they dispatch. Listen to
    // all the common ones and treat them as "recompute".
    var EVENTS = ['cart:updated', 'cart:refresh', 'cart:change', 'cart:added'];
    var onCartEvent = function () { sync(); };
    EVENTS.forEach(function (name) { document.addEventListener(name, onCartEvent); });
    self._onCartEvent = onCartEvent;
    self._cartEventNames = EVENTS;

    // Polling fallback for themes that don't emit cart events. Cheap (one
    // /cart.js request every 2s) and covers Publisher-theme drawer
    // refreshes that aren't observable from JS. Stored so disconnectedCallback
    // can stop it when the drawer re-render replaces this element.
    self._pollTimer = setInterval(function () {
      // Only poll when the panel is visible — saves request volume when
      // gifting isn't engaged.
      if (applied) sync();
    }, POLL_INTERVAL_MS);

    // Initial state — modal closed, applied flag derived from cart by sync().
    // If sync() finds an existing gift-fee line on init (e.g. customer
    // re-opened the drawer mid-session), it'll flip applied=true via
    // setApplied + updateDrawerRows.
    updateDrawerRows();
    writeCardSlug();
    // Fire-and-forget cart-services config fetch — may flip giftEligible (and
    // re-hide the idle CTA) once the tenant's collection scope resolves. Safe by
    // default: until/unless it resolves to "not eligible", the offer shows.
    loadCartServicesConfig();
    // Fire-and-forget CPDE-presence probe — caches cpdeAvailable, which
    // gates openCpdeDialog() (a card whose CPDE design is unreachable
    // won't open the customise dialog).
    probeCpde();
    setProp('_gift_card_design_id', getOrCreateDesignId());
    sync();
  };

  // Drawer re-renders replace this element with a fresh one; release this
  // (now-detached) instance's poll timer + cart-event listeners so it can't
  // keep reacting as a zombie. The window 'message' listener and the
  // isConnected guard in sync() cover any straggler safely.
  BookClubGiftWrap.prototype.disconnectedCallback = function () {
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
    if (this._onCartEvent && this._cartEventNames) {
      var handler = this._onCartEvent;
      this._cartEventNames.forEach(function (name) { document.removeEventListener(name, handler); });
      this._onCartEvent = null;
    }
  };

  if (!customElements.get('bookclub-gift-wrap')) {
    customElements.define('bookclub-gift-wrap', BookClubGiftWrap);
  }
})();
